export interface ShopProductArticleNumberDto {
  code: string;
  label: string;
}

export interface ShopProductOptionDto {
  name: string;
  values: string[];
  allowsCustomValue: boolean;
  customValueTrigger?: string;
  customValueLabel?: string;
  customValueKey?: string;
  customValuePlaceholder?: string;
  customValueMaxLength?: number;
}

export interface ShopProductFaqDto {
  question: string;
  answer: string;
}

export interface ShopProductDto {
  id: string;
  slug: string;
  name: string;
  price: number;
  category: string;
  description: string;
  longDescription?: string;
  isFeatured: boolean;
  displayOrder: number;
  imageUrls: string[];
  articleNumbers: ShopProductArticleNumberDto[];
  options: ShopProductOptionDto[];
  faq: ShopProductFaqDto[];
  isSecondHandArticle: boolean;
  allowsDirectReservation: boolean;
  sourceName?: string;
  size?: string;
  comment?: string;
  hasExpressedInterest: boolean;
}

export interface ShopCatalogDto {
  products: ShopProductDto[];
}

export interface CreateUsedShopProductPayload {
  name: string;
  price: number;
  sourceName: string;
  size?: string;
  comment?: string;
}

export type ShopReservationStatus = "offen" | "bestaetigt" | "erledigt" | "storniert";

export interface ShopReservationItemPayload {
  productSlug: string;
  quantity: number;
  selectedOptions: Record<string, string>;
}

export interface ShopReservationItemDto {
  id: string;
  productSlug: string;
  productName: string;
  unitPrice: number;
  quantity: number;
  selectedOptionsSummary: string;
  articleNumbersSummary: string;
}

export interface ShopReservationUserDto {
  id: string;
  kleidlesnummer: string;
  name: string;
  vorname: string;
  email: string;
}

export interface ShopReservationDto {
  id: string;
  status: ShopReservationStatus;
  managerNote?: string;
  createdAtUtc: string;
  updatedAtUtc: string;
  user: ShopReservationUserDto;
  items: ShopReservationItemDto[];
}

export interface UpdateShopReservationItemPayload {
  id: string;
  unitPrice: number;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:5124";

async function parseApiError(response: Response, fallbackMessage: string): Promise<Error> {
  const error = await response.json().catch(() => null);
  return new Error(error?.detail ?? fallbackMessage);
}

export async function getShopCatalog(token?: string): Promise<ShopCatalogDto> {
  const response = await fetch(`${API_BASE_URL}/api/shop-products`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (!response.ok) {
    throw await parseApiError(response, "Shop-Katalog konnte nicht geladen werden.");
  }

  return response.json();
}

export async function createUsedShopProduct(
  token: string,
  payload: CreateUsedShopProductPayload
): Promise<ShopProductDto> {
  const response = await fetch(`${API_BASE_URL}/api/shop-products/used`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw await parseApiError(response, "Gebrauchtartikel konnte nicht angelegt werden.");
  }

  return response.json();
}

export async function deleteUsedShopProduct(token: string, productId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/shop-products/used/${productId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw await parseApiError(response, "Gebrauchtartikel konnte nicht entfernt werden.");
  }
}

export async function expressInterestInUsedShopProduct(token: string, productId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/shop-products/used/${productId}/interest`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw await parseApiError(response, "Interesse konnte nicht versendet werden.");
  }
}

export async function createShopReservation(
  token: string,
  items: ShopReservationItemPayload[]
): Promise<ShopReservationDto> {
  const response = await fetch(`${API_BASE_URL}/api/shop-reservations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ items }),
  });

  if (!response.ok) {
    throw await parseApiError(response, "Reservierung konnte nicht angelegt werden.");
  }

  return response.json();
}

export async function getShopReservationsForCurrentUser(token: string): Promise<ShopReservationDto[]> {
  const response = await fetch(`${API_BASE_URL}/api/shop-reservations/mine`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw await parseApiError(response, "Reservierungen konnten nicht geladen werden.");
  }

  return response.json();
}

export async function getShopReservationsForManager(token: string): Promise<ShopReservationDto[]> {
  const response = await fetch(`${API_BASE_URL}/api/shop-reservations/manage`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw await parseApiError(response, "Reservierungen konnten nicht geladen werden.");
  }

  return response.json();
}

export async function updateShopReservationAsManager(
  token: string,
  reservationId: string,
  payload: { status: ShopReservationStatus; managerNote?: string; items?: UpdateShopReservationItemPayload[] }
): Promise<ShopReservationDto> {
  const response = await fetch(`${API_BASE_URL}/api/shop-reservations/manage/${reservationId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw await parseApiError(response, "Reservierung konnte nicht aktualisiert werden.");
  }

  return response.json();
}
